import { useBuild, useJobsForStep } from "~/components/build/Show/lib/BuildContext";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Drawer } from "~/components/build/Show/components/Drawer";
import { useCallback } from "react";
import { ErrorBoundary } from "~/lib/Bugsnag";
import { ErrorView } from "~/components/shared/ErrorView";
import { RetryableJob } from "~/components/build/Show/lib/types";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";

import {
  shouldRenderJobDrawer,
  shouldRenderStepDrawer,
  JobContent,
  StepContent,
  resolveCurrentJob,
} from "~/components/build/Show/components/StepDrawer/resolveDrawerVariant";
import { DrawerActions } from "~/components/build/Show/components/StepDrawer/StepDrawerActions";
import {
  StepDrawerProvider,
  filterRetriesForJob,
} from "~/components/build/Show/components/StepDrawer/StepDrawerContext";

/** @deprecated Replaced by LazyDataStepDrawer — remove when ProgressiveBuildShow flag is fully rolled out. */
export function EagerDataStepDrawer({ stepOrJobId }: { stepOrJobId: string }) {
  const { build } = useBuild();
  const { stepsMap } = useSteps();
  const allJobs = useBuildStore((state) => state.jobs);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useQueryParams();

  // Look up both job and step unconditionally (Map.get is safe with any key).
  const job = allJobs.get(stepOrJobId);
  const step = stepsMap.get(stepOrJobId);

  // Compute stepUuid unconditionally for useJobsForStep.
  const stepUuid = job?.stepUuid || step?.uuid || "";
  const stepJobs = useJobsForStep<RetryableJob>(stepUuid);

  // Close the drawer and navigate back to the build view (ie. waterfall / canvas / summary).
  const handleClose = useCallback(() => {
    navigate(pathname);
  }, [pathname, navigate]);

  // The trigger/root step isn't a real step in the pipeline so for now we
  // just ignore it.
  if (stepOrJobId === "root") {
    return null;
  }

  if (params.open === "false") {
    return null;
  }

  // If the stepId is a job, render the job details.
  if (job && shouldRenderJobDrawer(job)) {
    const retries = filterRetriesForJob(stepJobs, job);

    return (
      <StepDrawerProvider job={job} jobs={[]} retries={retries}>
        <Drawer onClose={handleClose} actions={<DrawerActions />}>
          <ErrorBoundary FallbackComponent={ErrorView}>
            <JobContent job={job} />
          </ErrorBoundary>
        </Drawer>
      </StepDrawerProvider>
    );
  }

  // Redirect for unknown steps.
  if (!step) {
    return <Navigate to={`${build.path}`} />;
  }

  if (!shouldRenderStepDrawer(step)) {
    return null;
  }

  const currentJob = resolveCurrentJob(step, stepJobs);

  return (
    <StepDrawerProvider step={step} job={currentJob} jobs={stepJobs} retries={stepJobs}>
      <Drawer onClose={handleClose} actions={<DrawerActions />}>
        <ErrorBoundary FallbackComponent={ErrorView}>
          <StepContent step={step} />
        </ErrorBoundary>
      </Drawer>
    </StepDrawerProvider>
  );
}
